import { resolveFileUrl } from "../../utils/fileUrl";

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

export const userName = (user) => (user ? user.name || user.email : "Unknown user");

export const initials = (name = "") =>
  name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

// Direct chats are titled after the other person; groups by their name.
export const otherMemberId = (conversation, meId) =>
  conversation?.members.find((m) => m.userId !== meId)?.userId;

export const conversationTitle = (conversation, users, meId) => {
  if (!conversation) return "";
  if (conversation.type === "group") return conversation.name || "Group";
  return userName(users[otherMemberId(conversation, meId)]);
};

export { resolveFileUrl };

export const isImage = (mimeType) => /^image\/(png|jpe?g|gif|webp)$/.test(mimeType || "");
export const isPdf = (mimeType) => mimeType === "application/pdf";
export const isAudio = (mimeType) => /^audio\//.test(mimeType || "");

export const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
// Attachments the in-app viewer can show; everything else is download-only.
export const isViewable = (mimeType) => isImage(mimeType) || isPdf(mimeType);

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// Short timestamp for conversation lists: time today, weekday this week, otherwise the date.
export const formatListTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  if (sameDay(date, now)) return formatTime(date);
  const days = (now - date) / 86400000;
  if (days < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const formatDayLabel = (value) => {
  const date = new Date(value);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, now)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

export const isSameDay = (a, b) => sameDay(new Date(a), new Date(b));

export const formatLastSeen = (user) => {
  if (!user) return "";
  if (user.online) return "Online";
  if (!user.lastSeenAt) return "Offline";
  const minutes = Math.round((Date.now() - new Date(user.lastSeenAt)) / 60000);
  if (minutes < 1) return "Last seen just now";
  if (minutes < 60) return `Last seen ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Last seen ${hours} h ago`;
  return `Last seen ${new Date(user.lastSeenAt).toLocaleDateString([], { month: "short", day: "numeric" })}`;
};

const names = (ids, users, meId) =>
  (ids || []).map((id) => (id === meId ? "you" : userName(users[id]))).join(", ");

// Text for system messages (group events), also used as list previews.
export const systemMessageText = (message, users, meId) => {
  const { action, actorId, targetIds, to } = message.metadata || {};
  const actor = actorId === meId ? "You" : userName(users[actorId]);
  switch (action) {
    case "group_created":
      return `${actor} created the group`;
    case "group_renamed":
      return `${actor} renamed the group to "${to}"`;
    case "members_added":
      return `${actor} added ${names(targetIds, users, meId)}`;
    case "member_removed":
      return `${actor} removed ${names(targetIds, users, meId)}`;
    case "member_left":
      return `${actor} left the group`;
    case "group_photo_changed":
      return `${actor} changed the group photo`;
    case "group_photo_removed":
      return `${actor} removed the group photo`;
    case "message_pinned":
      return `${actor} pinned a message`;
    default:
      return "Group updated";
  }
};

// ---------- Mentions ----------
// Message bodies carry mentions as <@userId> tokens; they are shown as "@Name" using current names.
export const MENTION_TOKEN = /<@(\d+)>/g;

export const mentionsToText = (text, users) =>
  (text || "").replace(/<@everyone>/g, "@everyone").replace(MENTION_TOKEN, (_, id) => `@${userName(users[Number(id)])}`);

// Converts "@Name" mentions picked in the composer back into tokens before sending.
export const textToMentions = (text, mentions) => {
  let result = text;
  [...mentions]
    .sort((a, b) => b.name.length - a.name.length)
    .forEach(({ id, name }) => {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      result = result.replace(new RegExp(`@${escaped}(?![\\p{L}\\p{N}])`, "gu"), `<@${id}>`);
    });
  return result;
};

export const messagePreview = (message, users, meId) => {
  if (!message) return "No messages yet";
  if (message.type === "system") return systemMessageText(message, users, meId);
  if (message.deletedAt) return "Message deleted";
  const prefix = message.senderId === meId ? "You: " : "";
  if (message.body) return prefix + mentionsToText(message.body, users).replace(/\s+/g, " ");
  const count = message.attachments?.length || 0;
  if (count === 1 && isAudio(message.attachments[0].mimeType)) return `${prefix}🎤 Voice message`;
  return `${prefix}📎 ${count === 1 ? message.attachments[0].fileName : `${count} files`}`;
};

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

// Splits text into plain strings and { url } parts so links can be rendered as anchors.
export const splitLinks = (text) =>
  text.split(URL_PATTERN).map((part, i) => (i % 2 === 1 ? { url: part } : part));

// Splits a message body into plain strings, { url } links and { mention: userId } parts.
export const splitMessage = (text) =>
  text
    .split(/(<@\d+>|<@everyone>)/g)
    .flatMap((chunk) => {
      if (chunk === "<@everyone>") return [{ mention: "everyone" }];
      const mention = /^<@(\d+)>$/.exec(chunk);
      return mention ? [{ mention: Number(mention[1]) }] : splitLinks(chunk);
    })
    .filter((part) => part !== "");
